import React, { useState } from "react";
import { ILoginRequest, IConfirmResponse } from "@interfaces/auth";
import { auth } from "@services/auth/auth-service";
import { handleApiError } from "@services/api";
import * as z from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "@components/common/withRouter";
import useAppState from "../../contexts/state";
import toast from "react-hot-toast";
import { Button, Divider, Input, Link } from "@heroui/react";
import { Icon } from '@iconify/react';
import { Formik, FormikHelpers } from "formik";
import { toFormikValidationSchema } from 'zod-formik-adapter';
import SocialSignIn from "@/components/auth/social-sign-in";
import { AuthHeader } from "@/layouts/non-auth-layout";
import { usePermanentStore } from "@/contexts/permanentStore";

const LoginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Email is invalid"),
  password: z.string().min(1, "Password is required"),
});

const Login = () => {
  const [searchParams] = useSearchParams();
  const isWebsiteMode = searchParams.get("ghost") === "true";

  document.title = isWebsiteMode ? "Login" : "Login to Cobrief";

  const { setSession } = useAppState((state: any) => ({
    setSession: state.setSession,
  }));

  const { setShouldShowAutoFeedbackModal } = usePermanentStore((s) => ({
    setShouldShowAutoFeedbackModal: s.setShouldShowAutoFeedbackModal,
  }));

  const navigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const onSubmit = async (
    values: ILoginRequest,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    try {
      setSubmitting(true);
      const response = await auth.login(values);
      const { user, session } = response.data as IConfirmResponse;
      setSession(user, session);

      setShouldShowAutoFeedbackModal(true);

      if (isWebsiteMode) {
        // Send message to parent window
        window.parent.postMessage({
          type: 'AUTH_SUCCESS',
          data: {
            user,
            session
          }
        }, '*');
      } else {
        const redirect = searchParams.get("redirect");
        navigate(redirect || "/dashboard");
      }

    } catch (e) {
      switch (handleApiError(e)) {
        case "Invalid login credentials":
          toast.error("Invalid login credentials. Please try again.");
          break;
        case "Email not confirmed":
          toast.error(
            "Please confirm your email address. Check your email for the confirmation link.",
          );
          break;
        default:
          toast.error("Something went wrong. Please try again.");
          break;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <AuthHeader
        title={isWebsiteMode ? "Welcome" : "Welcome Back"}
        subtitle={isWebsiteMode ? "Log in to continue" : "Log in to your account to continue"} />

      <SocialSignIn />

      <div className="flex items-center gap-4 py-2">
        <Divider className="flex-1" />
        <p className="shrink-0 text-tiny text-default-500">OR</p>
        <Divider className="flex-1" />
      </div>

      <Formik
        initialValues={{
          email: "",
          password: "",
        }}
        onSubmit={onSubmit}
        validationSchema={toFormikValidationSchema(LoginSchema)}>
        {({
          errors,
          touched,
          handleSubmit,
          handleChange,
          isSubmitting,
          values,
        }) => (
          <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
            <Input
              id="email"
              label="Email Address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              value={values.email}
              onChange={handleChange}
              isInvalid={errors?.email != null}
              errorMessage={touched?.email && errors?.email ? errors.email : ""} />
            <Input
              id="password"
              label="Password"
              name="password"
              placeholder="Enter your password"
              type={isPasswordVisible ? "text" : "password"}
              variant="bordered"
              value={values.password}
              onChange={handleChange}
              isInvalid={errors?.password != null}
              errorMessage={touched?.password && errors?.password ? errors.password : ""}
              endContent={
                <button type="button" onClick={() => setIsPasswordVisible(prev => !prev)}>
                  {isPasswordVisible ? (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-closed-linear"
                    />
                  ) : (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-bold"
                    />
                  )}
                </button>
              }
            />
            <div className="flex justify-end px-1 py-2">
              <Link className="text-default-500" size="sm" href="/forgot-password">
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              isLoading={isSubmitting}
              onClick={() => handleSubmit()}
              style={{ backgroundColor: "#c6f648" }}>
              Log In
            </Button>
          </form>
        )}
      </Formik>

      <p className="text-center text-small text-default-500">
        Need to create an account?&nbsp;
        <Link href={`/sign-up${isWebsiteMode ? '?ghost=true' : ''}`} size="sm" style={{ color: "#000" }}>
          Sign Up
        </Link>
      </p>
    </React.Fragment>
  )
}

export default withRouter(Login);
